import type { Dispatch, SetStateAction } from "react";
import {
  ActionMenu,
  Button,
  StatusPill,
  Tooltip,
  TruncatedText,
  type DataTableColumn,
} from "@playbookai/ui";
import * as api from "../../../api/admin-ai-profiles-api.js";
import type { LifecycleTarget } from "./LifecycleDialog.js";

/** The execution-profiles table's columns. Same shape and same reasoning as
    `connectionColumns`: the row actions drive page-owned state. */
export function profileColumns({
  setProfileEditor,
  setProfileTest,
  setLifecycle,
  controlsBusy,
  action,
}: {
  setProfileEditor: Dispatch<
    SetStateAction<api.ExecutionProfile | "new" | null>
  >;
  setProfileTest: Dispatch<SetStateAction<api.ExecutionProfile | null>>;
  setLifecycle: Dispatch<SetStateAction<LifecycleTarget | null>>;
  controlsBusy: boolean;
  action: (
    id: string,
    execute: () => Promise<unknown>,
    message: string,
  ) => Promise<void>;
}): DataTableColumn<api.ExecutionProfile>[] {
  return [
    {
      id: "name",
      width: "20%",
      /* Column floors (PB-5436): each is this column's width at a 1024px window
         with the rail closed, so the table's minimum is its 1024px width. A
         narrower box (the Player AI rail at 1024-1152px) scrolls inside the
         pinned region instead of clipping; wider boxes are unchanged. */
      minWidth: 139,
      header: "Profile",
      cardRole: "title",
      cell: (row) => (
        <div className="flex flex-col leading-tight">
          {/* eslint-disable-next-line ds/no-hand-built -- kept: the entity-link look (weight 500, cyan, underline on hover only) has no Button option; Button variant="link" size="inline" underlines at rest and inherits the weight, a look change no PR 3 row approves. */}
          <button
            type="button"
            className="w-fit font-medium text-pb-accent-text hover:underline focus-ring"
            disabled={controlsBusy}
            onClick={() => setProfileEditor(row)}
          >
            {row.display_name}
          </button>
          {/* The type lives on the wrapper: TruncatedText takes layout only. */}
          <span className="block max-w-[24rem] text-dense-1 text-pb-text-muted">
            <TruncatedText>{row.description}</TruncatedText>
          </span>
        </div>
      ),
    },
    {
      id: "connection",
      width: "17%",
      minWidth: 118,
      header: "Connection",
      cell: (row) => row.connection?.display_name ?? "Unavailable connection",
    },
    {
      id: "status",
      width: "12%",
      minWidth: 83,
      header: "Status",
      cell: (row) => (
        <StatusPill tone={row.available ? "healthy" : "neutral"}>
          {row.available
            ? "Available"
            : row.enabled
              ? "Unavailable"
              : "Disabled"}
        </StatusPill>
      ),
    },
    {
      id: "model",
      width: "15%",
      minWidth: 104,
      header: "Model",
      cell: (row) => (
        <span>
          {row.connection?.model_display_name ?? row.catalogue_model_id}
          <br />
          {row.locked ? "Locked" : "Editable draft"}
        </span>
      ),
    },
    {
      id: "qualification",
      width: "18%",
      minWidth: 125,
      header: "Qualification",
      cell: (row) => (
        <div>
          <span>{row.qualification?.status?.replaceAll("_", " ")}</span>
          {row.latest_test && (
            <div className="max-w-xs text-dense-1 text-pb-text-muted">
              <TruncatedText>
                {`${row.latest_test.state}: ${row.latest_test.message}`}
              </TruncatedText>
            </div>
          )}
          {!row.enabled && row.allowed_actions?.enable?.reason && (
            <div className="max-w-xs text-dense-1 text-pb-text-muted">
              <TruncatedText>{row.allowed_actions.enable.reason}</TruncatedText>
            </div>
          )}
        </div>
      ),
    },
    {
      id: "actions",
      width: "18%",
      minWidth: 125,
      header: "Actions",
      cell: (row) => (
        <div className="flex flex-nowrap items-center gap-2 whitespace-nowrap">
          {/* The design-system Tooltip in place of the native title (d1): its
              hover target is the wrapper, so it still shows while the button
              is disabled. */}
          <Tooltip
            content={row.allowed_actions?.test?.reason ?? ""}
            disabled={!row.allowed_actions?.test?.reason}
          >
            <Button
              size="sm"
              variant="secondary"
              disabled={
                controlsBusy ||
                (!["pending", "running"].includes(
                  row.latest_test?.state ?? "",
                ) &&
                  !row.allowed_actions?.test?.allowed)
              }
              onClick={() => setProfileTest(row)}
            >
              {["pending", "running"].includes(row.latest_test?.state ?? "")
                ? "View test"
                : "Test profile"}
            </Button>
          </Tooltip>
          <ActionMenu
            triggerLabel={`Actions for profile ${row.display_name}`}
            triggerSize="sm"
            items={[
              {
                id: "edit",
                label: "Edit",
                ...(controlsBusy
                  ? { disabledReason: "A change is in progress." }
                  : {}),
                onSelect: () => setProfileEditor(row),
              },
              {
                id: "duplicate",
                label: row.locked ? "Create replacement" : "Duplicate",
                ...(controlsBusy
                  ? { disabledReason: "A change is in progress." }
                  : {}),
                onSelect: () =>
                  void action(
                    row.id,
                    async () => {
                      const copy = await api.duplicateConfiguration(
                        "profiles",
                        row.id,
                      );
                      setProfileEditor(copy as api.ExecutionProfile);
                    },
                    "Profile duplicated as a draft.",
                  ),
              },
              {
                id: "lifecycle",
                label: row.enabled ? "Disable" : "Enable",
                ...(controlsBusy
                  ? { disabledReason: "A change is in progress." }
                  : !row.enabled && !row.allowed_actions?.enable?.allowed
                    ? {
                        disabledReason:
                          row.allowed_actions?.enable?.reason ??
                          "Test this profile before enabling it.",
                      }
                    : {}),
                onSelect: () =>
                  row.enabled
                    ? setLifecycle({ kind: "profiles", row, action: "disable" })
                    : void action(
                        row.id,
                        () => api.changeLifecycle("profiles", row, "enable"),
                        "Profile enabled.",
                      ),
              },
              {
                id: "delete",
                label: "Delete…",
                tone: "critical",
                separatorBefore: true,
                ...(controlsBusy
                  ? { disabledReason: "A change is in progress." }
                  : {}),
                onSelect: () =>
                  setLifecycle({ kind: "profiles", row, action: "delete" }),
              },
            ]}
          />
        </div>
      ),
    },
  ];
}
